import React, { useState } from 'react';

const ForensicServices = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with Forensic Services.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with Forensic Services.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Forensic Services
            </h1>

            <p className="mt-4 text-xl text-blue-100">
              Expert forensic investigation and litigation support services to detect, investigate, and prevent fraud, financial misconduct, and corporate disputes. Our forensic specialists provide evidence-based solutions for legal and regulatory matters.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">🔍</span> Investigate Suspected Fraud
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Our Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">What We Need</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview of Forensic Services
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Professional forensic services involve the application of investigative and analytical skills to resolve financial, legal, and corporate issues. Our forensic experts combine accounting, auditing, and investigative skills to examine evidence and provide expert opinions in legal proceedings and corporate investigations.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive forensic services include fraud investigations, financial misrepresentation analysis, asset tracing, dispute advisory, and expert witness testimony. We employ advanced data analytics, digital forensics, and investigative techniques to uncover facts and provide court-admissible evidence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you're dealing with suspected employee fraud, shareholder disputes, regulatory investigations, or complex commercial litigation, our forensic specialists provide the technical expertise and investigative rigor needed to protect your interests and support legal outcomes.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Features & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Fraud Investigation & Detection:</strong> Comprehensive investigation of financial statement fraud, asset misappropriation, and corruption schemes.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Digital Forensics:</strong> Advanced electronic evidence recovery, data analysis, and cybercrime investigation capabilities.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Expert Witness Testimony:</strong> Court-qualified expert witnesses providing independent, objective opinions in legal proceedings.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Asset Tracing & Recovery:</strong> Identification and tracing of hidden assets in fraud cases and commercial disputes.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Quantification of Damages:</strong> Financial analysis to quantify economic losses in disputes and insurance claims.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Regulatory Compliance Investigations:</strong> Investigation of regulatory breaches and compliance with anti-fraud legislation.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Forensic Data Analytics:</strong> Sophisticated data mining and analysis to identify patterns, anomalies, and red flags.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with Forensic Services." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Our Comprehensive Process
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              8 Steps for Forensic Investigation
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 grid-flow-row-dense">
            {[
              { number: "01", icon: "📋", title: "Case Assessment", desc: "Initial case evaluation, scope definition, and preservation of evidence to maintain chain of custody." },
              { number: "02", icon: "💾", title: "Evidence Collection", desc: "Systematic collection of financial records, electronic data, documents, and witness information." },
              { number: "03", icon: "💻", title: "Digital Forensics", desc: "Recovery and analysis of electronic evidence from computers, mobile devices, and cloud storage." },
              { number: "04", icon: "📊", title: "Data Analysis", desc: "Advanced data analytics to identify patterns, anomalies, and financial irregularities in large datasets." },
              { number: "05", icon: "🔍", title: "Financial Investigation", desc: "Detailed examination of financial transactions, bank records, and accounting entries for evidence." },
              { number: "06", icon: "👥", title: "Witness Interviews", desc: "Conducting structured interviews with relevant parties and documenting witness statements." },
              { number: "07", icon: "📝", title: "Report Preparation", desc: "Comprehensive forensic report with findings, evidence analysis, and expert conclusions." },
              { number: "08", icon: "⚖️", title: "Legal Support", desc: "Expert witness testimony, litigation support, and assistance with legal proceedings as required." }
            ].map((step, index) => (
              <div key={index} className="sm:col-span-2 lg:col-span-2 flex flex-col justify-between p-3 bg-white rounded-xl shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                <div className="flex flex-col flex-grow justify-center">
                  <span className="text-xl font-extrabold text-blue-600 block leading-tight">{step.number}</span>
                  <span className="text-2xl mx-auto block leading-none">{step.icon}</span>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug mt-0.5">{step.title}</h3>
                  <p className="text-gray-600 text-sm text-justify mt-1 leading-snug">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Information We Need
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Essential Forensic Documentation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "📄", title: "Financial Records", desc: "Bank statements, general ledgers, invoices, payment records, and financial statements for relevant periods." },
              { icon: "💾", title: "Electronic Evidence", desc: "Emails, digital documents, server logs, and access to relevant computer systems and mobile devices." },
              { icon: "👥", title: "Personnel Records", desc: "Employee records, organizational charts, job descriptions, and authority matrices for relevant staff." },
              { icon: "📋", title: "Policies & Procedures", desc: "Internal control documentation, authorization policies, and standard operating procedures." },
              { icon: "🛡️", title: "Legal Documents", desc: "Contracts, agreements, court filings, and any previous investigation reports or legal correspondence." },
              { icon: "💳", title: "Transaction Details", desc: "Specific transaction details, vendor information, customer records, and related party transactions." },
              { icon: "📹", title: "Surveillance Evidence", desc: "CCTV footage, access control logs, and any other surveillance or monitoring records if available." },
              { icon: "💬", title: "Communication Records", desc: "Phone records, chat logs, meeting minutes, and other communication evidence relevant to the case." },
              { icon: "📚", title: "Background Information", desc: "Company history, industry context, and any previous incidents or red flags that prompted investigation." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 text-justify">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Your Deliverables
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Akir Advisory Delivers
            </p>
          </div>

          {/* 5 equal boxes in a single row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: "📄", title: "Comprehensive Forensic Report", desc: "Detailed investigation report with findings, evidence analysis, methodology, and expert conclusions." },
              { icon: "💾", title: "Digital Evidence Package", desc: "Organized electronic evidence with proper chain of custody documentation for legal proceedings." },
              { icon: "💰", title: "Financial Quantification", desc: "Detailed calculation of financial losses, misappropriated amounts, and economic damages with supporting analysis." },
              { icon: "👥", title: "Witness Statements", desc: "Documented interview summaries, signed witness statements, and interview recordings where appropriate." },
              { icon: "⚖️", title: "Legal Support Package", desc: "Expert witness testimony, court-ready presentations, cross-examination preparation, and ongoing litigation support" }
            ].map((item, index) => (
              <div key={index} className="p-5 bg-white rounded-xl shadow-lg border-l-4 border-green-500 flex flex-col h-full">
                <span className="text-2xl mb-2 block">{item.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Common Questions
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      Forensic Services FAQs
                  </p>
              </div>
              
              <div className="space-y-4 max-w-4xl mx-auto">
                {[
                  { 
                    question: "When should a company consider engaging forensic services?", 
                    answer: "Companies should consider forensic services when they suspect fraud, face regulatory investigations, are involved in commercial disputes, need asset tracing, require expert witness testimony, or when conducting due diligence for mergers and acquisitions where fraud risk is identified." 
                  },
                  { 
                    question: "How long does a typical forensic investigation take?", 
                    answer: "Investigation timelines vary significantly based on complexity - simple cases may take 2-4 weeks, while complex multi-jurisdictional investigations can take 3-6 months or longer. We provide estimated timelines after initial case assessment and work efficiently to complete investigations promptly." 
                  },
                  { 
                    question: "What types of fraud do you typically investigate?", 
                    answer: "We investigate financial statement fraud, asset misappropriation (theft, embezzlement), corruption (bribery, kickbacks), payroll fraud, vendor fraud, intellectual property theft, cybercrime, money laundering, and Ponzi schemes across various industries and organizational sizes." 
                  },
                  { 
                    question: "How do you maintain confidentiality during sensitive investigations?", 
                    answer: "We implement strict confidentiality protocols including secure data handling, need-to-know access controls, encrypted communications, confidentiality agreements, and discrete investigation techniques to protect client interests and prevent evidence tampering." 
                  },
                  { 
                    question: "Can your forensic findings be used in court proceedings?", 
                    answer: "Yes, our forensic investigations are conducted to meet legal standards for evidence. We maintain proper chain of custody, document our methodology thoroughly, and our experts are qualified to provide court testimony. Our reports are prepared to withstand legal scrutiny and cross-examination." 
                  },
                  { 
                    question: "What qualifications do your forensic investigators possess?", 
                    answer: "Our team includes Certified Fraud Examiners (CFE), Chartered Accountants with forensic specialization, Certified Forensic Accountants (Cr.FA), digital forensics experts, and professionals with law enforcement backgrounds. We maintain ongoing training in investigation techniques and legal requirements." 
                  }
                ].map((faq, index) => (
                  <div key={index} className="border rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
          </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white">
                Need Expert Forensic Investigation?
            </h2>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
                Let our experienced forensic specialists help you uncover the truth, gather evidence, and protect your organization from fraud and financial misconduct.
            </p>
            <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                <span className="mr-2">🔍</span> Start Forensic Investigation
            </a>
        </div>
      </section>
    </main>
  );
};

export default ForensicServices;